import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import LockIcon from "@mui/icons-material/Lock";
import Box from "@mui/material/Box";
import { SwitchProps, Theme } from "@mui/material";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { JSX } from "react";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import { PermissionToggle } from "permissions/_views/tables/_components/permission-toggle";
import {
  isPermissionsRow,
  selectEffectivePermission,
  makeVirtualPermission,
  getControlMeta,
  classNameFromResource,
  getCardStyle,
  getGrantorName,
  getResourceBaseColor,
} from "permissions/_views/tables/_utilities/utils";
import {
  PackedItem,
  PermissionCardHeader,
  PermissionTitleContainer,
  SinglePermissionActions,
  SmallSelect,
} from "permissions/_views/tables/permissions-table-styles";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import {
  UniversalCellConfig,
  CardType,
  CategoryLabel,
  ControlMeta,
  MetaControlType,
} from "permissions/_views/types/permission-types";
import { useSession } from "users/__session/session-context";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";

export type SinglePermissionCardProps = {
  row: PermissionsByGrantee;
  config: Extract<UniversalCellConfig, { cardType: CardType.Single }>;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  onToggle(permission: Permission | undefined): void;
  onApplyMeta(effective: Permission, resource: Resource, meta: ControlMeta, newValue: string): void;
  individuals: Individual[];
  loadingIndividualUserIds: Guid[];
  getCardSubheader(resource: Resource): string | undefined;
};

export default function SinglePermissionCard(props: Readonly<SinglePermissionCardProps>): JSX.Element {
  const {
    row,
    config,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    theme,
    activeCategory,
    onToggle,
    onApplyMeta,
    individuals,
    loadingIndividualUserIds,
    getCardSubheader,
  } = props;

  const session = useSession();

  let permission: Permission | undefined;

  if (isPermissionsRow(row)) {
    const candidates = config.candidates(row, config.resource);
    permission = selectEffectivePermission(candidates as Permission[]);

    // If top-level, synthesize a virtual permission from template
    if (!permission && config.template) {
      const template = config.template(config.resource);
      if (template) permission = makeVirtualPermission(row.granteeUserId, template);
    }
  }

  if (!permission) return <Icon>remove</Icon>;

  const resource: Resource = permission.resource;
  const isDisabled = permission.isDisabled;
  const isVirtual = permission.isVirtual;
  const isOwnPermission = permission.granteeUserId.isEqualTo(session.user?.id);

  const accessLevelMeta: ControlMeta | null = getControlMeta(resource, MetaControlType.AccessLevel);
  const secondMeta: ControlMeta | null =
    getControlMeta(resource, MetaControlType.AccessType) ?? getControlMeta(resource, MetaControlType.Scope);

  let cardTitle = config.title?.({ resource: config.resource, permission });
  if (!cardTitle && isSingleGranteeView) cardTitle = classNameFromResource(config.resource);
  if (!cardTitle && !isSingleGranteeView) cardTitle = isDisabled ? "Disabled" : "Enabled";

  const cardSubHeader = getCardSubheader(resource);

  const { baseColor, bgColor, borderColor, textColor, display, selectSx } = getCardStyle(
    theme,
    resource,
    permission.isDisabled,
    activeCategory
  );

  function getInfoIcon(permission: Permission): JSX.Element {
    const grantor = getGrantorName(permission, individuals, loadingIndividualUserIds);
    const date = permission.createdDate?.format("D/M/YYYY");
    const time = permission.createdDate?.format("h:mm A");
    const title = date && time ? `Granted by ${grantor} on ${date} at ${time}` : `Granted by ${grantor}`;

    return (
      <Tooltip title={title}>
        <span style={{ cursor: "help" }}>
          <IconButton disabled size="small" edge="end" sx={{ p: 0 }}>
            <InfoOutlineIcon sx={{ color: baseColor }} fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <PackedItem
      isSingleGranteeView={isSingleGranteeView}
      isCreateTemplateView={isCreateTemplateView}
      isManageTemplatesView={isManageTemplatesView}
      cols={config.colSpan ?? 2}
      rows={config.rowSpan ?? 2}
      sx={{ backgroundColor: bgColor, borderColor, color: textColor, display }}
      variant="outlined"
    >
      <PermissionCardHeader
        avatar={
          <Tooltip title={isOwnPermission ? "You cannot change your own permissions" : ""} disableInteractive>
            <span>
              {isOwnPermission ? (
                <LockIcon fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <PermissionToggle
                  checked={!permission.isVirtual && !permission.disabledDate}
                  disabled={isManageTemplatesView || isOwnPermission}
                  onChange={() => onToggle(permission)}
                  color={getResourceBaseColor(permission.resource) as SwitchProps["color"]}
                />
              )}
            </span>
          </Tooltip>
        }
        title={
          <PermissionTitleContainer grouped={false}>
            {cardTitle}
            {!isVirtual && <Box sx={{ ml: "auto", p: 0 }}>{getInfoIcon(permission)}</Box>}
          </PermissionTitleContainer>
        }
        subheader={cardSubHeader}
        slotProps={{
          subheader: {
            sx: {
              title: cardSubHeader,
              maxWidth: "150px",
              overflow: "hidden",
              textAlign: "left",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
            },
          },
        }}
      />
      <SinglePermissionActions>
        <Stack direction="column" sx={{ gap: 1 }}>
          {accessLevelMeta && (
            <SmallSelect
              size="small"
              disabled={isVirtual || isDisabled || isOwnPermission}
              sx={selectSx}
              value={accessLevelMeta.value}
              onChange={(e) => onApplyMeta(permission, resource, accessLevelMeta, String(e.target.value))}
            >
              {accessLevelMeta.options.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </SmallSelect>
          )}
          {secondMeta && (
            <SmallSelect
              size="small"
              disabled={isVirtual || isDisabled || isOwnPermission}
              sx={selectSx}
              value={secondMeta.value}
              onChange={(e) => onApplyMeta(permission, resource, secondMeta, String(e.target.value))}
            >
              {secondMeta.options.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </SmallSelect>
          )}
        </Stack>
      </SinglePermissionActions>
    </PackedItem>
  );
}
